import { FC, ReactNode, useMemo, useState } from "react";
import { MarkdownV1SessionPart, SessionEventKind } from "../session.types";
import styled from "@emotion/styled";
import { MDSSessionPartContainer } from "@/mds/components/sessions/session-parts/session-part.style";
import {
  SessionPartProps,
  SessionPartContext,
  SessionPartConfig,
} from "@/mds/components/sessions/session-parts/session-part.types";
import { MDSBubble } from "@/mds/components/bubble/bubble";
import {
  MDSBubbleAlignment,
  MDSBubbleVariant,
  MDSBubbleSlotName,
} from "@/mds/components/bubble/bubble.types";
import {
  MemRoutableItemKind,
  OnClickMemItem,
  OnMemItemOut,
  OnMemItemOver,
  syntaxTreeModule,
} from "@mem-labs/common-editor";
import {
  useIsFirstPartInSessionGroup,
  useIsLastPartInSessionGroup,
  useIsLastUserMessagePartInSessionHistory,
} from "@/mds/components/sessions/session-context/mds-session-context.hooks";

import { MDSSessionSentBadge } from "../session-sent-badge";
import { MDSBubbleSessionSenderIcon } from "../session-sender-icon";
import { SessionMarkdownContent } from "@/mds/components/sessions/session-parts/common/session-markdown-content";
import { MemCommonMentionKind } from "@mem-labs/common-editor";
import { MDSSessionFeedback } from "../session-feedback/session-feedback";
import { MDSSessionContextContainer } from "@/mds/components/sessions/session-parts/session-context-container";
import { MDSIconButton, MDSTooltip, MDSTooltipPlacement, MDSIconButtonVariant } from "@/mds";
import { MdsIconKind } from "@/design-system/components/icon";

type Props = SessionPartProps<MarkdownV1SessionPart>;

export const MDSSessionMarkdownV1Part: FC<Props> = ({ part, context, config }) => {
  const isFirstInGroup = useIsFirstPartInSessionGroup({ part });
  const isLastInGroup = useIsLastPartInSessionGroup({ part });
  const isLastUserMessage = useIsLastUserMessagePartInSessionHistory({ part });
  const [isHovered, setIsHovered] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  const WrapperComponent: FC<IWrapperComponentProps> = (() => {
    if (
      context.eventKind === SessionEventKind.AgentActionV1 ||
      context.eventKind === SessionEventKind.AgentPlanningV1
    ) {
      return AgentActionWrapper;
    }

    return StandardWrapper;
  })();

  const onClickHandler: OnClickMemItem = (kind, id, target, event) => {
    if (!config.linkHandler) {
      return;
    }

    let mentionKind: MemCommonMentionKind | null = null;
    switch (kind) {
      case MemRoutableItemKind.NOTES:
        mentionKind = MemCommonMentionKind.Note;
        break;
      case MemRoutableItemKind.COLLECTIONS:
        mentionKind = MemCommonMentionKind.Collection;
        break;
      case MemRoutableItemKind["TIME-RANGES"]:
        mentionKind = MemCommonMentionKind.TimeRange;
        break;
      default:
        mentionKind = null;
        break;
    }

    config.linkHandler(mentionKind, id, target, event);
  };

  const handleMemItemOver: OnMemItemOver = (kind, itemId, target) => {
    if (!config.onMemItemOver) {
      return;
    }

    let mentionKind: MemCommonMentionKind | null = null;
    switch (kind) {
      case MemRoutableItemKind.NOTES:
        mentionKind = MemCommonMentionKind.Note;
        break;
      case MemRoutableItemKind.COLLECTIONS:
        mentionKind = MemCommonMentionKind.Collection;
        break;
      case MemRoutableItemKind["TIME-RANGES"]:
        mentionKind = MemCommonMentionKind.TimeRange;
        break;
      default:
        mentionKind = null;
        break;
    }

    config.onMemItemOver?.(mentionKind, itemId, target);
  };

  const handleMemItemOut: OnMemItemOut = () => {
    config.onMemItemOut?.();
  };

  // Convert MDX to standard Markdown for proper link parsing
  const content = syntaxTreeModule.convertMdxToMarkdown({
    mdxContent: part.content,
  });

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const meta: Record<string, unknown> | undefined = part._meta;

  const isMessageSent = useMemo(() => {
    return meta?.status === "sent";
  }, [meta?.status]);

  const isSentBadgeRenderable = useMemo(() => {
    if (context.eventKind !== SessionEventKind.UserActionV1) {
      return false;
    }

    return isLastInGroup && isLastUserMessage;
  }, [isLastInGroup, isLastUserMessage, context.eventKind]);

  const isSentBadgeVisible = useMemo(() => {
    if (!isSentBadgeRenderable) {
      return false;
    }

    return isMessageSent;
  }, [isSentBadgeRenderable, isMessageSent]);

  // Show feedback for agent messages (responses from the AI)
  const shouldShowActions = useMemo(() => {
    return (
      (context.eventKind === SessionEventKind.AgentActionV1 ||
        context.eventKind === SessionEventKind.AgentPlanningV1) &&
      isLastInGroup
    );
  }, [context.eventKind, isLastInGroup]);

  const handleCopyMessage = async () => {
    try {
      const html = syntaxTreeModule.convertMdxToHtml({
        mdxContent: part.content,
      });

      const item = new ClipboardItem({
        ["text/html"]: new Blob([html], { type: "text/html" }),
        ["text/plain"]: new Blob([part.content], { type: "text/plain" }),
      });

      await navigator.clipboard.write([item]);

      setIsCopied(true);
    } catch (error) {
      console.error(error);
    }

    setTimeout(() => setIsCopied(false), 1000);
  };

  return (
    <Wrapper onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
      <WrapperComponent
        isFirstInGroup={isFirstInGroup}
        isLastInGroup={isLastInGroup}
        context={context}
        config={config}
      >
        <Container eventKind={context.eventKind}>
          <SessionMarkdownContent
            onClickMemItem={onClickHandler}
            onMemItemOver={handleMemItemOver}
            onMemItemOut={handleMemItemOut}
            config={config}
            content={content}
            renderLinksAsChips={context.eventKind === SessionEventKind.UserActionV1}
          />
        </Container>
        {shouldShowActions && (
          <ActionsContainer isVisible={isHovered || !!context.feedbackGiven}>
            <MDSTooltip
              config={{
                label: "Copy",
                placement: MDSTooltipPlacement.Top,
              }}
            >
              <MDSIconButton
                iconKind={isCopied ? MdsIconKind.CopySolid : MdsIconKind.Copy}
                onClick={handleCopyMessage}
                variant={MDSIconButtonVariant.Transparent}
              />
            </MDSTooltip>
            {config.feedbackHandler && (
              <MDSSessionFeedback
                eventId={context.eventId}
                eventKind={context.eventKind}
                onFeedback={config.feedbackHandler}
                feedbackGiven={context.feedbackGiven}
              />
            )}
          </ActionsContainer>
        )}
      </WrapperComponent>
      {isSentBadgeRenderable && <MDSSessionSentBadge isVisible={isSentBadgeVisible} label="Sent" />}
    </Wrapper>
  );
};

interface IWrapperComponentProps {
  children: ReactNode;
  isFirstInGroup: boolean;
  isLastInGroup: boolean;
  context: SessionPartContext;
  config: SessionPartConfig;
}

const AgentActionWrapper: FC<IWrapperComponentProps> = ({
  children,
  isFirstInGroup,
  isLastInGroup,
  context: _context,
  config: _config,
}) => {
  return (
    <Bubble
      variant={MDSBubbleVariant.Transparent}
      align={MDSBubbleAlignment.Left}
      isLastInGroup={isLastInGroup}
    >
      <MDSBubbleSessionSenderIcon slot={MDSBubbleSlotName.Icon} isVisible={isFirstInGroup} />
      <AgentMarkdownContentWrapper isFirstInGroup={isFirstInGroup}>
        {children}
      </AgentMarkdownContentWrapper>
    </Bubble>
  );
};

const StandardWrapper: FC<IWrapperComponentProps> = ({
  children,
  isLastInGroup,
  context,
  config,
}) => {
  // Extract context mentions for user messages
  const mentions =
    context.eventKind === SessionEventKind.UserActionV1 ? context.mentions : undefined;

  return (
    <Bubble
      variant={MDSBubbleVariant.MessageUser}
      align={MDSBubbleAlignment.Right}
      isLastInGroup={isLastInGroup}
    >
      {mentions && <MDSSessionContextContainer mentions={mentions} config={config} />}
      {children}
    </Bubble>
  );
};

const Bubble = styled(MDSBubble)`
  width: 100%;
`;

const Container = styled(MDSSessionPartContainer)<{
  eventKind: SessionEventKind;
}>`
  align-items: center;
  margin-bottom: ${({ theme, eventKind }) =>
    eventKind === SessionEventKind.AgentActionV1 || eventKind === SessionEventKind.AgentPlanningV1
      ? theme.spacing.xs
      : "0"};
`;

const Wrapper = styled.div`
  width: 100%;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const AgentMarkdownContentWrapper = styled.div<{ isFirstInGroup: boolean }>`
  margin-top: ${({ isFirstInGroup, theme }) => (isFirstInGroup ? theme.spacing.xs : "0")};
`;

const ActionsContainer = styled.div<{ isVisible: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  transition: opacity 0.2s ease-in-out;
`;
